<script lang="ts">
    import { onMount, createEventDispatcher } from 'svelte';
    import { separatedList } from '@amp/web-app-components/src/utils/separated-list';
    import { TEXT_DIRECTION } from '@amp/web-app-components/src/constants';
    import allowDrop, {
        DropTarget,
    } from '@amp/web-app-components/src/actions/allow-drop';

    type TrackLockupLayoutKind =
        | 'shelfTrackList'
        | 'albumTrackList'
        | 'playlistTrackList'
        | 'recordingTrackList'
        | 'compilationTrackList';

    let isRestricted: boolean = false;
    let isRTL: boolean = false;

    const dispatch = createEventDispatcher();

    onMount(async () => {
        isRTL = document.dir === TEXT_DIRECTION.RTL;

        // If a specific track is preselected with `i` query param at mount,
        // scroll it into view.
        if (isSelected) {
            rowElement.scrollIntoView({ block: 'center' });
        }
    });

    export let item;
    export let layoutKind: TrackLockupLayoutKind;
    export let rowIndex: number;
    export let isFirst: boolean;
    export let isLast: boolean;
    export let isSelected: boolean;
    export let isLastSelected: boolean;
    export let showByline: boolean = false;
    export let showSecondColumn: boolean;
    export let showThirdColumn: boolean = false;
    export let showFourthColumn: boolean;
    export let ariaLabel: string | null = null;
    export let hasReactions: boolean = false;
    export let dropEnabled: boolean = false;
    export let showArtwork: boolean = false;
    export let hasSocialBadging: boolean = false;
    export let isDisabled: boolean = false;
    export let isPreview: boolean = false;
    // Playback state properties used for styling
    export let isLoading: boolean = false;
    export let isPlaying: boolean = false;
    export let isPaused: boolean = false;

    /**
     * itemSelector function provided by the parent app.
     */
    export let itemSelectorFn;

    // Used to determine if we need to display collaborators in its own column or not
    $: useReactionsLayout = showFourthColumn && hasReactions;
    $: useCollaboratorLayout = showFourthColumn && !hasReactions;
    $: useNonCollaborativeLayout =
        !useReactionsLayout && !useCollaboratorLayout;

    // $: isExplicit = item.showExplicitBadge;
    $: isExplicit = false;
    $: trackNumber = item.trackNumber;
    $: title = item.title;
    $: rankingText = item.rankingText;
    $: secondColumnData = item.subtitleLinks || [];
    $: thirdColumnData = item.tertiaryLinks || [];

    let rowElement: HTMLElement;

    // Use this flag to distinguish a focus caused by a click from a focus triggered by
    // other means, like keyboard navigation.
    let itemsHasJustBeenCmdClicked = false;
    function handleMouseDown(e: MouseEvent) {
        if (e.metaKey) {
            itemsHasJustBeenCmdClicked = true;
        }
    }

    // TODO: Investigate why some selection handlers do not work
    //       shift, up/down arrow, and cmd-A
    //       rdar://137428666 (Web Classical Music: Album/Playlist Detail Pages - Track/Work Select States)
    function handleKeydown(e: KeyboardEvent) {
        if (e.key === 'Enter' && e.target === rowElement) {
            // perform(itemWithUpdatedPlayAction.playAction);
            dispatch('keyDownPlayAction');
        } else if (e.key === 'Tab') {
            if (e.shiftKey) {
                if (document.activeElement === rowElement) {
                    if (isFirst) {
                        // Clear item selection focus exits the tracklist
                        itemSelectorFn.clearSelection();
                    } else {
                        itemSelectorFn.selectPreviousItem(item);
                        e.preventDefault();
                    }
                }
            } else {
                const focusableElements = rowElement.querySelectorAll(
                    'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
                );
                if (
                    document.activeElement ===
                    focusableElements[focusableElements.length - 1]
                ) {
                    if (isLast) {
                        // Clear item selection focus exits the tracklist
                        itemSelectorFn.clearSelection();
                    } else {
                        itemSelectorFn.selectNextItem(item);
                        e.preventDefault();
                    }
                }
            }
        }
    }

    function focusIf(node: HTMLElement, condition: boolean) {
        if (condition) {
            node.focus();
        }
        return {
            update(condition: boolean) {
                if (condition) {
                    node.focus();
                }
            },
        };
    }

    function handleClick(e: MouseEvent) {
        itemSelectorFn.handleClick(item, e);
    }

    function handleFocus() {
        // Set item selector when focus enters from top or bottom of tracklist
        // if there is not a current multi-selection.
        const numberOfSelectedItems = $itemSelectorFn.selectedItems.length;
        if (
            !itemsHasJustBeenCmdClicked &&
            numberOfSelectedItems <= 1 &&
            (isFirst || isLast)
        ) {
            itemSelectorFn.selectItem(item);
        }
        itemsHasJustBeenCmdClicked = false;
    }
</script>

<div
    class="songs-list-row drop-reset"
    class:is-loading={isLoading}
    class:is-playing={isPlaying}
    class:is-paused={isPaused}
    class:songs-list-row--disabled={isDisabled}
    class:songs-list-row--artwork={showArtwork}
    class:songs-list-row--no-artwork={!showArtwork}
    class:songs-list-row--decorated={showArtwork && hasSocialBadging}
    class:songs-list-row--two-lines={secondColumnData.length}
    class:songs-list-row--album={layoutKind === 'albumTrackList'}
    class:songs-list-row--playlist={layoutKind === 'playlistTrackList' ||
        layoutKind === 'recordingTrackList'}
    class:songs-list-row--compilation={layoutKind === 'compilationTrackList'}
    class:songs-list-row--selected={isSelected}
    class:songs-list-row--preview={isPreview}
    class:songs-list-row--alt={rowIndex % 2 === 0}
    class:disabled={isDisabled}
    bind:this={rowElement}
    on:dblclick={() => dispatch('trackListItemDoubleClick')}
    on:mousedown={(e) => {
        if (!e.metaKey) {
            itemSelectorFn.setCurrentMouseTarget(item);
        }
    }}
    on:mousedown={handleMouseDown}
    on:click={handleClick}
    on:keydown={handleKeydown}
    on:focusin={handleFocus}
    use:focusIf={isLastSelected}
    use:allowDrop={dropEnabled && {
        dropEnabled: true,
        targets: [DropTarget.Top, DropTarget.Bottom],
        onDrop: (...args) => dispatch('drop', ...args),
    }}
    role="row"
    tabindex="0"
    data-row={rowIndex}
    data-current-mouse-target={$itemSelectorFn.currentMouseTarget === item}
    data-testid="track-list-item"
    aria-label={ariaLabel}
    aria-rowindex={rowIndex + 1}
>
    <!-- Pre column: favorites state, middots and optional prefixed play states -->
    <div
        class="songs-list__col songs-list__col--prefix"
        data-testid="track-column-prefix"
        role="gridcell"
    >
        {#if $$slots['favorite-or-popular']}
            <slot name="favorite-or-popular" />
        {/if}

        {#if $$slots['prefixed-play-states']}
            <div class="prefixed-play-states">
                <slot name="prefixed-play-states" />
            </div>
        {/if}
    </div>

    <!-- first dynamic column: (this matches the list header firstColumnText) -->
    <!-- NOTE: this is a generic firstColumn. It might always be "title", but we can't say for sure. I didn't add the specific class. -->
    <div
        class="songs-list__col songs-list__col--song"
        data-testid="track-column-song"
        role="gridcell"
    >
        <div class="songs-list-row__song-container">
            {#if $$slots['track-video-icon']}
                <div class="songs-list-row__video-glyph">
                    <slot name="track-video-icon" />
                </div>
            {/if}

            <div
                class="songs-list-row__song-index"
                class:songs-list-row__song-index--ranking={rankingText}
                class:songs-list-row--with-collaborators={!useNonCollaborativeLayout}
            >
                {#if showArtwork && $$slots['artwork-with-badge']}
                    <slot name="artwork-with-badge" />
                {:else if trackNumber || trackNumber === 0}
                    <div
                        class="songs-list-row__column-data"
                        data-testid="track-number"
                    >
                        {trackNumber}
                    </div>
                {/if}

                {#if item.playAction && !isDisabled && $$slots['interactive-play-button']}
                    <div
                        class="songs-list-row__play-button-wrapper"
                        data-testid="track-play-button"
                    >
                        <slot name="interactive-play-button" />
                    </div>
                {/if}
            </div>

            <div class="songs-list-row__song-wrapper">
                <div
                    class="songs-list-row__song-name-wrapper"
                    class:songs-list-row__song-name-wrapper--explicit={isExplicit}
                    data-testid="song-name-wrapper"
                >
                    {#if $$slots['song-click-action']}
                        <slot name="song-click-action" />
                    {/if}

                    <!-- Combine the secondary column into the first column for `xsmall` view on Album pages or when reactions are visible on collab playlists -->
                    {#if $$slots['second-column-data-xs']}
                        <slot name="second-column-data-xs" />
                    {/if}

                    <!--
                        More general "byline" slot for data under the track title, for all viewports.
                        The `second-column-data-xs` slot above can be combined with this one at some
                        point, but leaving it for now to make the eventual Music/Podcasts migration
                        to this shared component more straightforward.
                    -->
                    {#if $$slots['first-column-byline'] && showByline}
                        <div
                            class="songs-list-row__by-line"
                            dir="auto"
                            data-testid="track-title-by-line"
                        >
                            <slot name="first-column-byline" />
                        </div>
                    {/if}
                </div>
            </div>

            {#if rankingText}
                <div class="songs-list-row__rank" data-testid="track-ranking">
                    {rankingText}
                </div>
            {/if}
        </div>
    </div>

    <!-- Hide the Secondary column on Album pages, where Secondary column is combined with Songs Column, for `xsmall` view-->
    {#if showSecondColumn && layoutKind !== 'albumTrackList'}
        <!-- second dynamic column (this matches the list header secondColumnText) -->
        <div
            class="songs-list__col songs-list__col--secondary"
            data-testid="track-column-secondary"
            role="gridcell"
        >
            <!-- TODO: rdar://89005006 (JMOTW: RTL: Line Clamp styling for BIDI text) -->
            <div class="songs-list__song-link-wrapper" dir="auto">
                {#if $$slots['second-column-data']}
                    <slot name="second-column-data" />
                {/if}
            </div>
        </div>
    {/if}

    {#if showThirdColumn}
        <!-- third dynamic column (this matches the list header thirdColumnText) -->
        <div
            class="songs-list__col songs-list__col--tertiary"
            data-testid="track-column-tertiary"
            role="gridcell"
        >
            <div
                class="songs-list__song-link-wrapper"
                dir={!useReactionsLayout ? 'auto' : undefined}
            >
                {#if $$slots['third-column-data']}
                    <slot name="third-column-data" />
                {/if}
            </div>
        </div>
    {/if}

    {#if showFourthColumn}
        <!-- fourth dynamic column (collaborative playlist - added by/reactions) -->
        <div
            class="songs-list__col songs-list__col--quaternary"
            class:songs-list__reactions={hasReactions}
            data-testid="track-column-quaternary"
            role="gridcell"
        >
            <div
                class="songs-list__song-link-wrapper"
                dir={useReactionsLayout ? 'auto' : undefined}
            >
                {#if $$slots['fourth-column-data']}
                    <slot name="fourth-column-data" />
                {/if}
            </div>
        </div>
    {/if}

    <!-- last column: duration + controls (library, contextual menu, preview button) -->
    <div
        class="songs-list__col songs-list__col--time"
        data-testid="track-column-controls"
        role="gridcell"
    >
        <div class="songs-list-row__controls">
            {#if $$slots['last-column-data']}
                <slot name="last-column-data" />
            {/if}
        </div>
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'amp/stylekit/core/specs' as *;
    @use 'amp/stylekit/core/colors' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;
    @use '@amp/web-shared-styles/app/core/mixins/tabular-numbers' as *;
    @use '@amp/web-shared-styles/app/core/mixins/transgray-scrim' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use 'sass:color';

    // TODO: rdar://89617186 (JMOTW: UXD: Tracklist: investigate how we can have shared styling between different files)
    @use './styles/shared.scss' as *;

    // Mixins for differentiating tracks that are part of (or not part of)
    // classical/works albums. Classical album tracks are always the two-line
    // height (even when only one line is displayed) and never emulate a second
    // column in wider viewports.
    @mixin is-classical-album {
        :global(.songs-list--album.songs-list--has-composer-header) & {
            @content;
        }
    }

    @mixin is-nonclassical-album {
        :global(.songs-list--album:not(.songs-list--has-composer-header)) & {
            @content;
        }
    }

    $decorated-artwork-size: 32px; // track artwork with social badge
    $decorated-play-button-scale: 0.75;

    .songs-list-row {
        --platterBorderColor: var(--pageBG);
        background-color: var(--rowBackgroundColor, transparent);

        // Classical album tracks have a taller row height
        @include is-classical-album {
            height: $songs-list-row-height-two-lines;
        }
    }

    // Alternating row color
    .songs-list-row--alt {
        --rowBackgroundColor: var(--tracklistAltRowColor);
        --platterBorderColor: var(
            --tracklistBadgeBorderColor
        ); // --tracklistAltRowColor without any opacity
    }

    // Hide artwork with badge at medium viewport if row has collaborators and show artwork without badge at medium viewports and up
    .songs-list-row--with-collaborators {
        :global(.is-viewport-small) {
            display: grid;
            grid-area: song-artwork;

            @media (--medium) {
                display: none;
            }
        }

        :global(.is-viewport-large) {
            display: none;

            @media (--medium) {
                display: grid;
                grid-area: song-artwork;
            }
        }
    }

    // Adjust size of the play button for --decorated rows with collaborators that hide the badge on artwork at medium and larger widths
    .songs-list-row--decorated {
        .songs-list-row--with-collaborators {
            @media (--medium) {
                :global(
                        .is-viewport-large
                            + .songs-list-row__play-button-wrapper
                    ) {
                    height: 100%;
                    width: 100%;

                    :global(svg) {
                        transform: unset;
                    }
                }
            }
        }
    }

    .songs-list-row__badge__contributor-wrapper {
        $contributor-badge-size: 24px; // social badge artwork
        $contributor-badge-border-color: var(--labelDivider);
        display: flex;
        align-items: center;

        :global(.badge-artwork) {
            --override-placeholder-bg-color: var(--profileBadgeBG);
            width: $contributor-badge-size;
            height: $contributor-badge-size;
            position: relative;
            border-radius: 50%;
            background-color: white;
            box-shadow: unset;
            pointer-events: none; // Don't interfere with play button tap area
            border: 0.5px solid $contributor-badge-border-color;
            flex-shrink: 0;
            z-index: calc(var(--z-default) + 2);
        }

        :global(.songs-list-row__badge-name) {
            margin-inline-start: 6px;
        }
    }

    .songs-list__reactions {
        :global(.reaction-list) {
            display: flex;

            :global(li) {
                width: 100%;
            }
        }
    }

    // Playlists and nonclassical album tracks with supplemental artist
    // information have a taller row height
    .songs-list-row--playlist,
    .songs-list-row--two-lines {
        height: $songs-list-row-height-two-lines;

        // No second line in wider viewports, on nonclassical album pages,
        // since we emulate the extra Playlist columns instead of stacking,
        // in wider viewports
        @media (--small) {
            @include is-nonclassical-album {
                height: $songs-list-row-height;
            }
        }
    }

    .songs-list-row__song-container {
        height: $songs-list-row-height;
        display: grid;
        grid-template-areas: 'song-artwork song-rank song-icon song-name';
        grid-template-columns: auto auto auto 1fr;
        align-items: center;

        .songs-list-row--artwork & {
            padding-inline-start: $songs-list-start-padding;
        }
    }

    .songs-list-row__video-glyph {
        position: relative;
        display: inline-block;
        width: 19px;
        vertical-align: text-bottom;
        fill: var(--keyColor);
        grid-area: song-icon;
        z-index: var(--z-default);
        margin-inline-start: 3px;
        margin-inline-end: 10px;

        :global(svg) {
            vertical-align: bottom;
        }

        .songs-list-row--selected & {
            fill: white;
        }
    }

    // Container for track index, play button, album/social artwork
    .songs-list-row__song-index {
        position: relative;
        color: var(--systemSecondary);
        display: grid;

        .songs-list-row--playlist & {
            margin-inline-end: 12px;
        }

        .songs-list-row--decorated & {
            margin-inline-end: 7px;
        }

        .songs-list-row--no-artwork & {
            margin-top: 1px;
        }

        // Always show play button and hide index when loading/playing/paused
        .songs-list-row.is-loading &,
        .songs-list-row.is-playing &,
        .songs-list-row.is-paused & {
            --playButtonOpacity: 1;
        }
    }

    // Show play button while loading or playing
    .songs-list-row.is-loading .prefixed-play-states,
    .songs-list-row.is-playing .prefixed-play-states {
        --playButtonOpacity: 1;
    }

    // Rank (in charts)
    .songs-list-row__rank {
        grid-area: song-rank;
        width: 32px;
        text-align: start;
        font: var(--body-emphasized-tall);
    }

    // Track number
    .songs-list-row__column-data {
        width: $songs-list-play-button-area-footprint;
        grid-area: song-index;
        opacity: calc(1 - var(--playButtonOpacity));
        text-align: center;
        font: var(--body-tall);
    }

    .songs-list-row__play-button-wrapper {
        grid-area: 1 / 1;
        opacity: var(--playButtonOpacity, 0);
        z-index: calc(var(--z-default) + 2); // = z-index: 3;

        // Play button covers entire wrapper area
        :global(.interactive-play-button),
        :global(.play-button) {
            width: 100%;
            height: 100%;
            vertical-align: top;
        }

        // Include scrim when track artwork is present
        .songs-list-row--artwork & {
            --playButtonIconHoverColor: white;
            border-radius: var(
                --global-border-radius-small,
                #{$global-border-radius-small}
            );

            @include transgray-scrim(tracklist);
        }

        // Song with social badge
        .songs-list-row--decorated & {
            width: $decorated-artwork-size;
            height: $decorated-artwork-size;

            :global(svg) {
                transform: scale($decorated-play-button-scale);
            }
        }

        // Song with no artwork (track number only)
        .songs-list-row--no-artwork & {
            --playButtonIconHoverColor: #{var(--keyColor)};
            --playButtonIconColor: #{var(--keyColor)};
            --nonPlatterIconFill: #{var(--keyColor)};
        }
    }

    .songs-list__song-link-wrapper {
        :global(span),
        :global(a),
        & {
            @include line-clamp;

            @include rtl {
                // Force `text-align: right` for RTL pages
                text-align: right;
            }

            @include ltr {
                // Force `text-align: left` for LTR pages
                text-align: left;
            }
        }

        // Switches display of Sliding Modal and Popover triggers for reactions
        :global(.is-viewport-large) {
            display: none;

            @media (--small) {
                display: flex;
            }

            :global(.popover-toggle) {
                padding: 0;

                :global(.popover-toggle__button) {
                    background-color: unset;
                    padding: 0;
                }

                :global(.popover-toggle__popover) {
                    line-height: unset;
                    width: 320px;

                    &::before {
                        display: none;
                    }
                }
            }
        }

        :global(.is-viewport-small) {
            display: flex;
            justify-content: flex-end;

            @media (--small) {
                display: none;
            }

            :global(.reactions-sliding-modal-wrapper) {
                width: auto;
            }
        }
    }

    .songs-list-row__song-wrapper {
        width: 100%;
        display: inline-flex;
        grid-area: song-name;
        align-items: center;
        line-height: $body-line-height;
        overflow: hidden;

        @media (--small) {
            padding-inline-end: 10px;
        }
    }

    .songs-list-row__song-name-wrapper,
    .songs-list-row__explicit-wrapper {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-rows: repeat(2, auto);
    }

    .songs-list-row__song-name-wrapper {
        flex: 1;
        overflow: hidden;
        cursor: default;

        // Turn off RTL/LTR BIDI override for this element.
        @include rtl {
            text-align: right;
        }

        @include ltr {
            // Force `left` and `right` styles for LTR pages
            text-align: left;
        }

        @media (--small) {
            // Emulate the extra Playlist columns, instead of stacking, in wider viewports
            .songs-list-row--two-lines & {
                @include is-nonclassical-album {
                    display: inline-grid;
                    grid-template-columns: 56% auto;
                    align-items: center;
                    column-gap: $songs-list-cell-end-padding;
                }
            }
        }

        :global(.click-action) {
            overflow: hidden;
        }
    }

    .songs-list-row__explicit-wrapper {
        --explicitBadgeSize: 9px;
        max-width: 100%;
        align-items: baseline;

        @include line-clamp;

        @include rtl {
            // Lock order of text and explicit icon to RTL direction (explicit icon on left)
            // Note: `dir="auto"` in the HTML ensures our text truncates on the correct end of string
            direction: rtl;
        }

        .songs-list-row__badge.songs-list-row__badge--explicit {
            // Specificity overwrite
            &::before {
                content: '\a0'; // Add 1 character space between text and icon
            }
        }
    }

    // Show focus rings around line-clamped artist and album links
    .songs-list__song-link-wrapper,
    .songs-list-row__song-wrapper,
    .songs-list-row__song-name-wrapper,
    .songs-list-row__by-line {
        @include overflow-bleed($focus-size);
    }

    .songs-list-row__song-name,
    .songs-list-row__by-line {
        @include line-clamp;

        @include rtl {
            // Force `text-align: right` for RTL pages
            text-align: right;
        }

        @include ltr {
            // Force `text-align: left` for LTR pages
            text-align: left;
        }

        // Adds em-dash and spaces between
        // segments of the by-line
        > *:not(:last-child)::after {
            content: '\2004\2014\2004';
            display: inline;

            @include rtl {
                // Recommendation from the RTL team: Make the em-dash strongly RTL. It's weak
                // directionality may cause the line to become partially LTR in GoldenGateE Safari.
                content: '\2004\200F\2014\2004';
            }
        }

        :global(a) {
            display: inline;
        }
    }

    .songs-list-row__by-line {
        color: var(--systemSecondary);
        grid-row-start: 2;
        grid-column: span 2;

        // Space to spec, but compensate for focus ring overflow bleed
        margin-top: 2px - $focus-size;

        @include line-clamp;

        @media (--small) {
            margin-top: 1px - $focus-size;
        }
    }

    .songs-list-row__by-line__mobile {
        // Hide song second line on wider viewports
        @media (--small) {
            .songs-list-row--playlist & {
                display: none;
            }
        }
    }

    .songs-list-row__controls {
        display: inline-grid;
        grid-template-areas: 'song-controls-add song-controls-length song-controls-context';
        align-items: center;
        position: relative;
    }

    .songs-list-row__add-to-library {
        grid-area: song-controls-add;
        opacity: var(--addToLibraryOpacity, 0);
        margin-inline-end: 7px;
    }

    .songs-list-row__preview-button,
    .songs-list-row__length {
        grid-area: song-controls-length;
    }

    .songs-list-row__preview-button {
        --linkColor: var(
            --keyColor
        ); // Preview button component gets text color from --linkColor
        display: flex;
        position: relative;
        white-space: nowrap;

        // When preview button is present, it follows the same display
        // logic as play button
        opacity: var(--playButtonOpacity, 0);
        text-align: end;
        font: var(--caption-1-emphasized);
        z-index: var(--z-default);
    }

    .songs-list-row__length {
        display: inline-block;
        line-height: 38px;
        cursor: default;

        // Hide length when preview button is present and visible
        .songs-list-row--preview & {
            opacity: calc(1 - var(--playButtonOpacity, 0));
        }

        @include tabular-numbers;
    }

    .songs-list-row__context-menu {
        grid-area: song-controls-context;

        .songs-list-row__length + & {
            margin-inline-start: $songs-list-row-context-menu-spacer;
        }
    }

    //
    // Hover styles for unselected rows
    //
    .songs-list-row:not(.songs-list-row--disabled) {
        // Display Play and Add to Library buttons when interacting with row
        &:hover,
        &:focus-within,
        &:focus-visible {
            --playButtonOpacity: 1;
            --addToLibraryOpacity: 1;

            :global(.favorite-button) {
                --favoriteIconStarOutline: var(
                    --favoriteButtonStarOutline-hover,
                    var(--keyColor)
                );
                --favoriteButtonBackground: var(--pageBG);
            }
        }

        @supports #{'selector(:has(:focus-visible))'} {
            &:focus-within {
                --playButtonOpacity: 0;
                --addToLibraryOpacity: 0;
            }

            &:hover,
            &:focus-visible,
            &:has(:focus-visible) {
                --playButtonOpacity: 1;
                --addToLibraryOpacity: 1;
            }
        }

        // Override row hover color on unselected tracks
        &:not(.songs-list-row--selected):hover {
            --rowBackgroundColor: var(--tracklistHoverColor);
            --platterBorderColor: rgb(
                240,
                240,
                240
            ); // --tracklistHoverColor with the transparency

            @include is-classical-album {
                --tracklistHoverColor: var(--tracklistWorksTrackHoverColor);
            }
        }

        // Keep systemPrimary text color when the TrackList song name is an
        // <a> anchor element
        &:not(.songs-list-row--selected) {
            :global(.click-action:has(.songs-list-row__song-name)) {
                --linkColor: var(--systemPrimary);
            }
        }

        // The default hover styles greatly reduce the visibility of the reactions platter
        // in mobile view. When the browser window is at the smallest breakpoint &
        // the tracklist has reactions, use rollover background color on hover
        &:has(.songs-list__reactions):hover {
            @media (--range-xsmall-down) {
                --tracklistHoverColor: rgba(
                    198,
                    0,
                    7,
                    1
                ); // standard keyColor rollover
                --platterBorderColor: var(--tracklistHoverColor);
            }
        }
    }

    //
    // Selected Row Styles
    //
    @mixin selected-row-styles($color: rgb(255, 255, 255)) {
        --linkColor: #{$color};
        --explicitFillOverride: #{$color};
        --contextMenuEllipsisFillOverride: #{$color};
        --addToLibraryFillOverride: #{$color};
        color: $color;

        &.songs-list-row--no-artwork {
            .songs-list-row__play-button-wrapper {
                --playButtonIconHoverColor: #{$color};
                --playButtonIconColor: #{$color};
                --nonPlatterIconFill: #{$color};
            }
        }

        .songs-list__col--time,
        .songs-list__col--song,
        .songs-list__col--tertiary,
        .songs-list__col--quaternary,
        .songs-list-row__song-index,
        .songs-list-row__by-line,
        .songs-list-row__preview-button {
            --linkColor: #{$color};
            color: $color;
        }

        // Adjust focus outline color for links inside selected rows
        // The Favorite button isn't visually inside the selected row, so it's
        // excluded from this focus styling.
        // Buttons within the Popover component are also excluded to prevent
        // low contrast in light mode
        :global(
                :focus-visible:not(
                        .favorite-button,
                        .popover-toggle__popover button,
                        .prefixed-play-states button
                    )
            ) {
            @include focus-shadow(color.change($color, $alpha: 0.6));
        }
    }

    .songs-list-row--selected {
        --rowBackgroundColor: var(--selectionColor);
        --platterBorderColor: var(--selectionColor);
        outline: 0; // The focused item takes styling cues from the `selected` class

        @include selected-row-styles;
    }

    // When reactions are present, invert tracklist content colors on hover
    .songs-list-row:has(.songs-list__reactions):hover {
        @media (--range-xsmall-down) {
            @include selected-row-styles;
        }
    }

    .songs-list-row--disabled {
        .songs-list-row__song-index,
        .songs-list__col--song {
            color: var(--systemTertiary);
        }

        &.songs-list-row--selected {
            @include selected-row-styles(swatch(systemSecondary, dark));
        }
    }

    .songs-list-row.is-playing,
    .songs-list-row.is-paused {
        &:hover {
            :global(.playing-bars-hover) {
                opacity: 1;
            }

            :global(.playback-bars__svg) {
                opacity: 0;
            }
        }
    }

    // Remove preview button, add to library, and song duration from narrow
    // viewports to allow more space for song title before truncating.
    @media (--range-grid-layout-xs-1-sidebar-down) {
        .songs-list-row__preview-button,
        .songs-list-row__add-to-library,
        .songs-list-row__length {
            display: none;
        }
    }

    // Remove buttons that appear on hover on touchscreen devices.
    // (Actions are still available through the "more" contextual menu.)
    @media (--target-touchscreen) {
        .songs-list-row__preview-button,
        .songs-list-row__add-to-library {
            display: none;
        }
    }

    // Drag and Drop styling
    $track-drag-separator-offset: 4px; // stylelint-disable-line order/order

    :global([data-drop-area]).songs-list-row {
        &:global(.is-drag-over-top) {
            .songs-list__col--song {
                &::before {
                    content: '';
                    margin: auto;
                    width: 100%;
                    height: $drag-over-focus-size;
                    display: block;
                    position: absolute;
                    right: 0;
                    left: 0;
                    transform: translateY(-$track-drag-separator-offset);
                    background-color: var(--keyColor);
                    border-radius: 2px;
                    z-index: calc(
                        var(--z-default) + 3
                    ); // Set on the same level as `up-next__section`

                    @include target-safari {
                        width: calc(100% - (var(--bodyGutter) * 2));
                    }
                }
            }
        }

        &:global(.is-drag-over-bottom) {
            .songs-list__col--song {
                &::after {
                    content: '';
                    margin: auto;
                    width: 100%;
                    height: $drag-over-focus-size;
                    display: block;
                    position: absolute;
                    right: 0;
                    left: 0;
                    transform: translateY($track-drag-separator-offset);
                    background-color: var(--keyColor);
                    border-radius: 2px;
                    z-index: calc(
                        var(--z-default) + 3
                    ); // Set on the same level as `up-next__section`

                    @include target-safari {
                        width: calc(100% - (var(--bodyGutter) * 2));
                    }
                }
            }
        }
    }
</style>
